/* iter170 — Global Discover on/off + per-user admin bypass.
 *
 * Discover is considered "available" for a given user when EITHER:
 *   1. The global admin setting `discover_enabled` is "true"  (default),
 *      OR
 *   2. The user has role === "admin" (per-user admin flag).
 *
 * We cache the flag in localStorage so the value is available
 * synchronously (needed by `appMode.js#getMode()` on first paint) and
 * refresh it from `/api/public-settings` + `/api/auth/me` on every
 * app mount. A window event is dispatched on any change so subscribers
 * re-render immediately.
 *
 * IMPORTANT: default is TRUE (Discover on) — a fresh install / offline
 * device should NEVER accidentally lose access to Discover before the
 * first successful settings fetch completes.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { getMe } from "./api";

const BASE = process.env.REACT_APP_BACKEND_URL;
const KEY_ENABLED = "habano.discover_enabled";   // "true" | "false"
const KEY_IS_ADMIN = "habano.is_admin";          // "1" | "0"
const EVT = "habano:discover-availability-changed";

function _read(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function _write(key, val) {
  try { localStorage.setItem(key, val); } catch { /* ignore */ }
}
function _emit() {
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* ignore */ }
}

/** Sync — safe to call before any network fetch. Defaults to TRUE
 *  when no cache has been written yet (first launch). */
export function isDiscoverAvailableSync() {
  const enabled = _read(KEY_ENABLED);
  const isAdmin = _read(KEY_IS_ADMIN) === "1";
  if (isAdmin) return true;
  // If we've never fetched, assume TRUE so first paint isn't a wrong
  // downgrade. Once the first refresh completes we'll have a concrete
  // value.
  if (enabled === null) return true;
  return enabled === "true";
}

/** Fetch public settings + current user and refresh the cache. Emits
 *  the change event when either value flips. Never throws. */
export async function refreshDiscoverAvailability() {
  let enabled = _read(KEY_ENABLED);
  let isAdmin = _read(KEY_IS_ADMIN) === "1";
  try {
    const { data } = await axios.get(`${BASE}/api/public-settings`, { timeout: 6000 });
    const raw = (data && data.discover_enabled) || "true";
    const next = String(raw).toLowerCase() === "true" ? "true" : "false";
    if (next !== enabled) {
      _write(KEY_ENABLED, next);
      enabled = next;
    }
  } catch { /* keep cached */ }
  try {
    const me = await getMe().catch(() => null);
    const nextAdmin = me && me.role === "admin";
    const nextRaw = nextAdmin ? "1" : "0";
    if ((_read(KEY_IS_ADMIN) || "0") !== nextRaw) {
      _write(KEY_IS_ADMIN, nextRaw);
      isAdmin = nextAdmin;
    }
  } catch { /* keep cached */ }
  _emit();
  return { discover_enabled: enabled === "true", is_admin: isAdmin };
}

/** React hook — reactive read of the effective availability. */
export function useDiscoverAvailable() {
  const [avail, setAvail] = useState(() => isDiscoverAvailableSync());
  useEffect(() => {
    const handle = () => setAvail(isDiscoverAvailableSync());
    window.addEventListener(EVT, handle);
    window.addEventListener("storage", handle);
    return () => {
      window.removeEventListener(EVT, handle);
      window.removeEventListener("storage", handle);
    };
  }, []);
  return avail;
}
